import { X } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useServiceViewModel } from '../../view-model/professional/company/service-view-model';
import { useState, useEffect } from 'react';
import ServiceView from './service-view';
import ServiceStepper from './service-stepper';
import { Button } from '../ui/button';
import { optionCategoryViewModel } from '../../view-model/professional/company/option-category-view-model';
import { optionViewModel } from '../../view-model/professional//company/option-view-model';
import { useAutoCloseModal } from '../../hooks/useAutoCloseModal';

const ServiceModal = observer(() => {
  const viewModel = useServiceViewModel();
  const { t } = useTranslation();
  const [createdServiceId, setCreatedServiceId] = useState<string | null>(null);

  const isOpen = viewModel.isServiceModalOpen;
  const mode = viewModel.serviceModalMode;
  const service = viewModel.selectedService || undefined;

  // Enregistre ce modal pour être fermé automatiquement en cas d'erreur de limite
  useAutoCloseModal(isOpen, () => {
    cleanupOrphanCategories();
    viewModel.closeServiceModal();
    setCreatedServiceId(null);
  });
  
  // Fonction pour nettoyer les catégories sans options
  const cleanupOrphanCategories = () => {
    const serviceIdToCheck = createdServiceId || service?.id;
    
    if (serviceIdToCheck) {
      const categories = viewModel.getCategoriesByServiceId(serviceIdToCheck);
      
      categories.forEach(category => {
        const options = optionViewModel.getOptionsByCategoryId(category.id);
        const hasOptions = options.length > 0;
        
        if (!hasOptions) {
          optionCategoryViewModel.deleteCategory(category.id).catch(error => {
            console.error('Erreur lors de la suppression de la catégorie orpheline:', error);
          });
        }
      });
    }
  };
  
  useEffect(() => {
    if (!isOpen && (createdServiceId || service?.id)) {
      cleanupOrphanCategories();
    }
  }, [isOpen]);
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
            {mode === 'create' ? t('services.addService') :
             mode === 'edit' ? t('services.editService') :
             t('services.viewService')}
          </h3>
          <button
            type="button"
            onClick={() => {
              cleanupOrphanCategories();
              viewModel.closeServiceModal();
              setCreatedServiceId(null); // Réinitialiser l'ID du service créé
            }}
            className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          {mode === 'view' && service ? (
            <div className="p-6 space-y-4">
              <ServiceView serviceId={service.id} />
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    viewModel.closeServiceModal();
                  }}
                  className="mr-2"
                >
                  {t('services.close')}
                </Button>
              </div>
            </div>
          ) : (mode === 'edit' || mode === 'create') ? (
            <ServiceStepper
              mode={mode}
              service={service}
              onComplete={(serviceId) => {
                setCreatedServiceId(serviceId);
                cleanupOrphanCategories();
                viewModel.closeServiceModal();
              }}
              onCancel={() => {
                cleanupOrphanCategories();
                viewModel.closeServiceModal();
                setCreatedServiceId(null);
              }}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
});

export default ServiceModal;
